import { Fragment, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { findPhoneNumbersInText } from 'libphonenumber-js';

interface Props {
  message: string;
}

type FormattedItem = {
  display: ReactNode;
  startsAt: number;
  endsAt: number;
};

const getFormattedPhoneNumbers = (message: string): FormattedItem[] => {
  const phoneNumbers = findPhoneNumbersInText(message, 'US');
  return phoneNumbers.map((phoneNumber) => ({
    display: (
      <Link href={`tel:${phoneNumber.number.number}`}>
        {message.slice(phoneNumber.startsAt, phoneNumber.endsAt)}
      </Link>
    ),
    startsAt: phoneNumber.startsAt,
    endsAt: phoneNumber.endsAt,
  }));
};

const getFormattedNewLines = (message: string): FormattedItem[] => {
  if (!message.includes('\n')) return [];
  const regex = /\n/gm;
  const formattedNewLines: FormattedItem[] = [];
  let result: RegExpExecArray | null;
  while ((result = regex.exec(message))) {
    formattedNewLines.push({
      startsAt: result.index,
      endsAt: result.index + result[0].length,
      display: <br />,
    });
  }
  return formattedNewLines;
};

const getFormattedLinks = (message: string): FormattedItem[] => {
  const regex =
    /(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{2,6}\b(?:[-a-zA-Z0-9()@:%_+~#?&/=]*)/gm;
  const formattedLinks: FormattedItem[] = [];
  let result: RegExpExecArray | null;
  while ((result = regex.exec(message))) {
    const urlText = message.slice(
      result.index,
      result.index + result[0].length,
    );
    formattedLinks.push({
      startsAt: result.index,
      endsAt: result.index + result[0].length,
      display: (
        <Link
          target="_blank"
          href={urlText.startsWith('http') ? urlText : `//${urlText}`}
          rel="noopener noreferrer"
        >
          {urlText}
        </Link>
      ),
    });
  }
  return formattedLinks;
};

const FormattedMessage: React.FC<Props> = ({ message }) => {
  const [formattedItems, setFormattedItems] = useState<FormattedItem[]>([]);

  useEffect(() => {
    const newLines = getFormattedNewLines(message);
    const links = getFormattedLinks(message);
    const phoneNumbers = getFormattedPhoneNumbers(message);
    const formattedItems = [...newLines, ...links, ...phoneNumbers].sort(
      (a, b) => a.startsAt - b.startsAt,
    );
    setFormattedItems(formattedItems);
  }, [message]);

  if (!formattedItems.length) return message;
  return formattedItems.map((item: FormattedItem, i: number) => {
    const before =
      i < 1
        ? message.slice(0, item.startsAt)
        : message.slice(formattedItems[i - 1].endsAt, item.startsAt);
    const after = message.slice(item.endsAt);
    return (
      <Fragment key={(before || after).substring(0, 10)}>
        {before}
        {item.display}
        {i === formattedItems.length - 1 ? after : null}
      </Fragment>
    );
  });
};

const Link = styled.a`
  color: var(--font-color) !important;
  border-bottom: 1px dashed var(--font-color);
  text-decoration: none;
`;

export default FormattedMessage;
